// Third Party
import React, { Component } from 'react';

// Components
import { Checkbox, Input, SelectInput, SelectOption } from '../../../components/inputs';
import { SegmentedControl, ControlItem } from '../../../components/navigation';
import { PrimaryButton, CancelButton } from '../../../components/buttons';
import { HoldingIcon } from '../../../components/common';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { SCENARIO_TYPE, SCENARIO_UNIT, SCENARIO_FREQUENCY } from '../../../../utils/income-stream';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import ComparisonItem from '@/types/comparison-item';
import IncomeStreamScenario from '@/types/income-stream-scenario';

interface Props {
  item: ComparisonItem;
  isMobile?: boolean;
  scenario: IncomeStreamScenario;
  scenarioNumber: number;
  updateScenario: (scenario: IncomeStreamScenario) => void;
  handleSaveScenario: () => void;
  handleCancelScenarioEditing: () => void;
}

class IncomeStreamForm extends Component<Props> {
  handleAmountChange = (amount: string) => {
    const {
      scenario,
      scenario: { unit },
    } = this.props;

    let value = Number(amount);

    if (unit === SCENARIO_UNIT.PERCENT) {
      value = Math.min(Number(value), 99);
    }

    this.props.updateScenario({
      ...scenario,
      amount: value,
    });
  };

  handleHarvestAllChange = () => {
    const { scenario } = this.props;
    this.props.updateScenario({
      ...scenario,
      distributionHarvest: scenario.distributionHarvest === 100 ? 0 : 100,
    });
  };

  handleTypeChange = (type: SCENARIO_TYPE) => {
    const { scenario } = this.props;
    this.props.updateScenario({
      ...scenario,
      type,
    });
  };

  handleUnitChange = (unit: SCENARIO_UNIT) => {
    const { scenario } = this.props;
    this.props.updateScenario({
      ...scenario,
      unit,
      amount: unit === SCENARIO_UNIT.PERCENT && scenario.amount > 99 ? 99 : scenario.amount,
    });
  };

  handleFrequencyChange = (frequency: SCENARIO_FREQUENCY) => {
    const { scenario } = this.props;
    this.props.updateScenario({
      ...scenario,
      frequency,
    });
  };

  invalidInputs = () => {
    const { scenario } = this.props;

    return !scenario.amount && scenario.distributionHarvest === 0;
  };

  renderTypeDropdown = () => {
    const {
      scenario: { type },
    } = this.props;

    const options = Object.values(SCENARIO_TYPE).map(option => ({
      value: option,
      label: formatMessage(`Comparison.IncomeStream.Form.Type.${option}`),
    }));

    return (
      <SelectInput
        className="type"
        label={formatMessage('Comparison.IncomeStream.Form.Type.Title')}
        placeholder="--"
        selected={type}
        onValueChange={this.handleTypeChange}
        compact
      >
        {options.map(option => (
          <SelectOption key={option.value} value={option.value}>
            {option.label}
          </SelectOption>
        ))}
      </SelectInput>
    );
  };

  renderFrequencyDropdown = () => {
    const {
      scenario: { frequency },
    } = this.props;

    const options = Object.values(SCENARIO_FREQUENCY).map(option => ({
      value: option,
      label: formatMessage(`Comparison.IncomeStream.Form.Frequency.${option}`),
    }));

    return (
      <SelectInput
        className="frequency"
        label={formatMessage('Comparison.IncomeStream.Form.Frequency.Title')}
        placeholder="--"
        selected={frequency}
        onValueChange={this.handleFrequencyChange}
        compact
      >
        {options.map(option => (
          <SelectOption key={option.value} value={option.value}>
            {option.label}
          </SelectOption>
        ))}
      </SelectInput>
    );
  };

  renderActionButtons = () => (
    <>
      <CancelButton onClick={this.props.handleCancelScenarioEditing} />

      <PrimaryButton disabled={this.invalidInputs()} onClick={this.props.handleSaveScenario}>
        {formatMessage('Comparison.IncomeStream.Apply')}
      </PrimaryButton>
    </>
  );

  render() {
    const {
      scenario,
      scenario: { amount, unit },
      scenarioNumber,
      item,
      isMobile = false,
    } = this.props;

    return (
      <div className="income-stream-form">
        <div className="income-stream-form-header d-flex">
          <div className="scenario-title">
            <HoldingIcon type={COMPARISON_ITEM_TYPES.FUND} color={item.color} />

            <FormattedMessage id="Comparison.IncomeStream.Scenario" values={{ key: scenarioNumber }} />
          </div>
          {!isMobile && <>{this.renderActionButtons()}</>}
        </div>

        {this.renderTypeDropdown()}

        <div className="d-flex">
          <div className="amount-inputs">
            <div className="amount-title">
              <FormattedMessage id="Comparison.IncomeStream.Form.Amount.Title" />
            </div>

            <div className="d-flex">
              <Input
                name="amount"
                compact
                value={Math.abs(amount) || ''}
                type="number"
                min={0}
                max={unit === SCENARIO_UNIT.PERCENT ? 99 : undefined}
                onValueChange={this.handleAmountChange}
                autoComplete="off"
              />

              <SegmentedControl onChange={this.handleUnitChange} selected={unit}>
                <ControlItem value={SCENARIO_UNIT.DOLLAR}>
                  <span>{SCENARIO_UNIT.DOLLAR}</span>
                </ControlItem>
                <ControlItem value={SCENARIO_UNIT.PERCENT}>
                  <span>{SCENARIO_UNIT.PERCENT}</span>
                </ControlItem>
              </SegmentedControl>
            </div>
          </div>

          {this.renderFrequencyDropdown()}
        </div>

        <Checkbox
          className="harvest"
          selected={scenario.distributionHarvest === 100}
          label={formatMessage('Comparison.IncomeStream.Form.HarvestAll')}
          onChange={this.handleHarvestAllChange}
        />
      </div>
    );
  }
}

export default IncomeStreamForm;
